import { Select } from "@chakra-ui/select";
import React, { useEffect, useState } from "react";
import {
  sub,
  startOfWeek,
  previousSaturday,
  previousSunday,
  nextSaturday,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  format,
} from "date-fns";
import { enAU } from "date-fns/locale";

interface SelectPeriodProps {
  onChange: (
    startDate: string | undefined,
    endDate: string | undefined
  ) => void;
}

type PeriodType =
  | "AllTime"
  | "LastWeek"
  | "LastMonth"
  | "Last3Months"
  | "Last6Months"
  | "OneYear"
  | "YearToDate";

export const SelectPeriod = ({ onChange }: SelectPeriodProps) => {
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>("AllTime");

  const onPeriodChange = (periodType: PeriodType) => {
    setSelectedPeriod(periodType);
  };

  useEffect(() => {
    const currentDate = new Date();
    let startDate = "";
    let endDate = "";
    if (selectedPeriod === "AllTime") {
      onChange(undefined, undefined);
    }
    if (selectedPeriod === "LastWeek") {
      const lastWeekStartDate = previousSunday(sub(currentDate, { weeks: 1 }));
      const lastWeekEndDate = nextSaturday(lastWeekStartDate);
      startDate = format(lastWeekStartDate, "yyyy-MM-dd", { locale: enAU });
      endDate = format(lastWeekEndDate, "yyyy-MM-dd", { locale: enAU });
    }
    if (selectedPeriod === "LastMonth") {
      const lastMonthStartDate = startOfMonth(sub(currentDate, { months: 1 }));
      const lastMonthEndDate = endOfMonth(sub(currentDate, { months: 1 }));
      startDate = format(lastMonthStartDate, "yyyy-MM-dd", { locale: enAU });
      endDate = format(lastMonthEndDate, "yyyy-MM-dd", { locale: enAU });
    }
    if (selectedPeriod === "Last3Months") {
      const lastThreeMonthsStartDate = startOfMonth(
        sub(currentDate, { months: 3 })
      );
      const lastThreeMonthsEndDate = endOfMonth(
        sub(currentDate, { months: 1 })
      );
      startDate = format(lastThreeMonthsStartDate, "yyyy-MM-dd", {
        locale: enAU,
      });
      endDate = format(lastThreeMonthsEndDate, "yyyy-MM-dd", { locale: enAU });
    }
    if (selectedPeriod === "Last6Months") {
      const lastSixMonthsStartDate = startOfMonth(
        sub(currentDate, { months: 6 })
      );
      const lastSixMonthsEndDate = endOfMonth(sub(currentDate, { months: 1 }));
      startDate = format(lastSixMonthsStartDate, "yyyy-MM-dd", {
        locale: enAU,
      });
      endDate = format(lastSixMonthsEndDate, "yyyy-MM-dd", { locale: enAU });
    }
    if (selectedPeriod === "OneYear") {
      const lastYearStartDate = startOfYear(sub(currentDate, { years: 1 }));
      const lastYearEndDate = endOfYear(lastYearStartDate);
      startDate = format(lastYearStartDate, "yyyy-MM-dd", { locale: enAU });
      endDate = format(lastYearEndDate, "yyyy-MM-dd", { locale: enAU });
    }
    if (selectedPeriod === "YearToDate") {
      const ytdStartDate = startOfYear(currentDate);
      startDate = format(ytdStartDate, "yyyy-MM-dd", { locale: enAU });
      endDate = format(currentDate, "yyyy-MM-dd", { locale: enAU });
    }
    onChange(startDate, endDate);
  }, [selectedPeriod]);

  return (
    <Select
      size="sm"
      borderRadius="md"
      value={selectedPeriod}
      onChange={(e) => onPeriodChange(e.currentTarget.value as PeriodType)}
    >
      <option value="AllTime">All Time</option>
      <option value="LastWeek">Last Week</option>
      <option value="LastMonth">Last Month</option>
      <option value="Last3Months">Last 3 Months</option>
      <option value="OneYear">1 Year</option>
      <option value="YearToDate">Year to Date</option>
    </Select>
  );
};
